import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { Sparkles, MessageSquare, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { TypingIndicator } from '../ui/typing-indicator';
import { useTypewriter } from '../../hooks/useTypewriter';
import { useWhatsAppDemo } from '../../hooks/useWhatsAppDemo';
import {
  staggerContainer,
  messageReveal,
  floatLoop,
} from '../../lib/motion';

interface ChatMessage {
  from: 'user' | 'bot';
  text: string;
  time: string;
}

const CONVERSATION: ChatMessage[] = [
  { from: 'user', text: 'Ciao, vorrei prenotare un appuntamento', time: '14:30' },
  { from: 'bot', text: 'Certo! Per quale servizio e in che giorno?', time: '14:30' },
  { from: 'user', text: 'Taglio capelli, domani alle 15:00', time: '14:31' },
  {
    from: 'bot',
    text: 'Perfetto, appuntamento confermato per domani alle 15:00. Ti ho inviato i dettagli.',
    time: '14:31',
  },
];

const Hero: React.FC = () => {
  const handleDemoRequest = useWhatsAppDemo();
  const reduce = useReducedMotion();

  // Stream the headline accent phrase once, above the fold.
  const { text: accent, isDone } = useTypewriter('Conversazioni WhatsApp', {
    speed: 55,
    startDelay: 350,
  });

  // Reveal bubbles one at a time; show a typing indicator just before the
  // final bot reply.
  const [revealed, setRevealed] = useState(reduce ? CONVERSATION.length : 0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (reduce) {
      setRevealed(CONVERSATION.length);
      return;
    }

    setRevealed(0);
    setTyping(false);
    const timers: ReturnType<typeof setTimeout>[] = [];

    // First three messages appear in sequence.
    [900, 1700, 2500].forEach((delay, i) => {
      timers.push(setTimeout(() => setRevealed(i + 1), delay));
    });
    // Bot "thinks" before the last reply.
    timers.push(setTimeout(() => setTyping(true), 3100));
    timers.push(
      setTimeout(() => {
        setTyping(false);
        setRevealed(CONVERSATION.length);
      }, 4100)
    );

    return () => timers.forEach(clearTimeout);
  }, [reduce]);

  const scrollToDemo = () => {
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative overflow-hidden bg-background pt-20 lg:pt-28 pb-16 lg:pb-24"
    >
      {/* Soft brand glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-24 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl"
      />

      <div className="container relative mx-auto px-4">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: copy + primary CTA */}
          <div className="space-y-8">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl font-bold leading-tight text-text-primary lg:text-6xl"
            >
              Automatizza le{' '}
              <span
                className={
                  'bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent' +
                  (!isDone ? ' typewriter-caret' : '')
                }
              >
                {accent}
              </span>{' '}
              della tua azienda
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="text-xl leading-relaxed text-text-secondary lg:text-2xl"
            >
              Crea bot AI intelligenti che gestiscono i clienti 24/7, aumentano le
              vendite e migliorano il customer service.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col items-start gap-4 sm:flex-row sm:items-center"
            >
              <Button
                onClick={handleDemoRequest}
                variant="gradient"
                size="lg"
                className="rounded-full px-8 text-lg font-medium shadow-md transition-shadow hover:shadow-lg"
              >
                <Sparkles className="h-5 w-5" aria-hidden="true" />
                Richiedi Demo Gratuita
              </Button>

              <button
                type="button"
                onClick={scrollToDemo}
                className="group inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-base font-medium text-text-secondary transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                Guarda la demo
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </button>
            </motion.div>
          </div>

          {/* Right: live WhatsApp chat mockup */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative mx-auto max-w-sm lg:max-w-md">
              <div className="rounded-[3rem] border border-border bg-card p-2 shadow-md">
                <div className="overflow-hidden rounded-[2.5rem] bg-gray-900">
                  {/* Header bar */}
                  <div className="flex items-center gap-3 bg-primary px-4 py-3 text-white">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                      <MessageSquare className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold leading-tight">ChatLy Bot</h3>
                      <p className="flex items-center gap-1.5 text-xs opacity-90">
                        <span className="h-2 w-2 rounded-full bg-success" aria-hidden="true" />
                        Online
                      </p>
                    </div>
                  </div>

                  {/* Inner chat surface — intentionally dark in both themes */}
                  <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                    className="flex h-96 flex-col gap-3 overflow-hidden p-4 lg:h-[460px]"
                  >
                    {CONVERSATION.map((msg, i) =>
                      i < revealed ? (
                        <motion.div
                          key={i}
                          variants={messageReveal}
                          initial="hidden"
                          animate="visible"
                          className={
                            msg.from === 'user'
                              ? 'flex justify-end'
                              : 'flex justify-start'
                          }
                        >
                          <div
                            className={
                              'max-w-[80%] rounded-2xl px-3 py-2 shadow-sm ' +
                              (msg.from === 'user'
                                ? 'rounded-br-sm bg-primary text-white'
                                : 'rounded-bl-sm bg-white text-gray-900')
                            }
                          >
                            <p className="text-sm leading-snug">{msg.text}</p>
                            <span
                              className={
                                'mt-1 block text-right text-[10px] ' +
                                (msg.from === 'user'
                                  ? 'text-white/75'
                                  : 'text-gray-500')
                              }
                            >
                              {msg.time}
                            </span>
                          </div>
                        </motion.div>
                      ) : null
                    )}

                    <AnimatePresence>
                      {typing && (
                        <motion.div
                          key="typing"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          className="flex justify-start"
                        >
                          <div className="rounded-2xl rounded-bl-sm bg-white px-4 py-3 text-gray-500 shadow-sm">
                            <TypingIndicator />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
              </div>

              {/* Single subtle floating chip */}
              <motion.div
                {...floatLoop}
                className="absolute -bottom-5 -left-5 flex items-center gap-2 rounded-2xl border border-border bg-card px-3 py-2 shadow-md"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10">
                  <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                </span>
                <span className="text-sm font-medium text-text-primary">
                  Risposte AI istantanee
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
